/**
 * Client-side purchase-event dedup.
 *
 * The thank-you page fires `purchase` on every mount that carries order
 * params — refresh, back-button, receipt-link revisit, and the
 * generic -> /thank-you/[slug]/ product-picker handoff (which re-mounts
 * with the sessionStorage stash) all re-fired the same order. Verified in
 * PostHog (2026-07-22): one buyer produced ~15 purchase events across
 * 3 days for a single order, and nearly every real order appeared twice
 * (once on /thank-you/, once on the slugged page seconds later).
 *
 * Keys are scoped (`purchase:` / `identify:`) so the overlay tracker can
 * claim the purchase fire while the thank-you page still gets one
 * identify() for the same order. Stored in localStorage (not
 * sessionStorage) so email-receipt revisits in a NEW tab stay deduped.
 */

const STORAGE_KEY = "pm_tracked_orders_v1";

/** Keep the list bounded — a browser only ever sees a handful of orders. */
const MAX_ENTRIES = 30;

function readTrackedOrders(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    // localStorage can throw under privacy modes; fail open (no dedup).
    return [];
  }
}

export function hasTrackedOrder(scope: "purchase" | "identify", orderKey: string): boolean {
  return readTrackedOrders().includes(`${scope}:${orderKey}`);
}

export function markOrderTracked(scope: "purchase" | "identify", orderKey: string): void {
  if (typeof window === "undefined") return;
  try {
    const entries = readTrackedOrders();
    const entry = `${scope}:${orderKey}`;
    if (entries.includes(entry)) return;
    entries.push(entry);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // Tolerate storage failures silently — worst case is a re-fire,
    // which is the pre-dedup status quo.
  }
}
